<script lang="ts">
    import { openModal } from 'svelte-modals';
    import { onMount } from 'svelte';
    import { _ } from 'svelte-i18n';
    import { z } from 'zod';

    import { ResolutionDate } from '../../telematics/components/Colorizer/ResolutionDate';
    import { ResolutionTime } from '../../telematics/components/Colorizer/ResolutionTime';
    import { getHumanFriendlyDateDifferenceForToday } from '../../../utilities/datetime';
    import ButtonSmallBlueRounded from '../../../shared/ButtonSmallBlueRounded.svelte';
    import IconArrowRightAlt from '../../../shared/Icons/IconArrowRightAlt.svelte';
    import MessageNotFound from '../../../shared/Messages/MessageNotFound.svelte';
    import { api, clientSettings } from '../../../../stores/api';
    import { JobApi, JobHistoryEventResponseSchema } from '../JobApi';
    import ErrorInline from '../../../shared/ErrorInline.svelte';
    import IconExpander from '../../../shared/Icons/IconExpander.svelte';
    import SwitchView from '../../../shared/SwitchView.svelte';
    import JobHistoryWrapper from './JobHistoryWrapper.svelte';
    import ModalCustom from '../../../shared/Modals/ModalCustom.svelte';
    import type { JobHistoryEventResponseType } from '../JobApi';
    import Opacity from '../../../shared/Opacity.svelte';
    import Spinner from '../../../shared/Spinner/Spinner.svelte';
    import { JobHistoryItem } from './JobHistoryItem';
    import jsonExample from './Mock/jobHistoryConfirmArray.json';
    import { debug } from '../../../../stores/store';
    import type { JobResponseType } from '../types';
    import IconUndefined from '../../../shared/Icons/IconUndefined.svelte';
    import SuccessColor from '../../../shared/SuccessColor.svelte';
    import WarningColor from '../../../shared/WarningColor.svelte';
    import IconRemove from '../../../shared/Icons/IconRemove.svelte';
    import IconAdd from '../../../shared/Icons/IconAdd.svelte';
    import Switch from '../../../shared/Switch.svelte';
    import { JobHistoryApp } from './JobHistoryApp';
    import JobHistoryDescription from './JobHistoryDescription.svelte';
    import JobHistoryEventSource from './JobHistoryEventSource.svelte';

    export let jobId: number;
    export let extended: boolean = false;
    export let job: JobResponseType | undefined = undefined; // needed to reloadData if job has been changed

    // let jobHistoryList: JobHistoryItem[] = [];
    let jobHistoryListVisible: JobHistoryItem[] = [];
    let showLimit = 5;
    let loading: boolean = true;
    let error: string = '';
    let expandedAll: boolean = false;
    let showDetails: boolean = false;

    let jobHistoryApp = new JobHistoryApp();

    function reloadData() {
        loading = true;
        Promise.all([jobHistoryApp.load(jobId)])
            .catch(e => {
                console.error(e);
                error = $_('Failed to load job history');
            })
            .finally(() => {
                loading = false;
                jobHistoryListVisible = jobHistoryApp.getJobHistoryListVisible(
                    extended ? null : 5,
                    !$debug
                );
            });
    }

    function getFormattedEventTime(eventTime: string) {
        try {
            // 2021-09-29T08:00:00 or 2021-09-29 08:00:00 or 2021-09-29 08:00:00.123456
            if (
                !z
                    .string()
                    .regex(/^\d{4}-\d{2}-\d{2}(T|\s)\d{2}:\d{2}:\d{2}(\.\d+)?$/)
                    .safeParse(eventTime).success
            ) {
                return eventTime; // not a valid date
            }
            let minutesUnix = Math.round(
                new Date(eventTime).getTime() / 1000 / 60 - new Date().getTimezoneOffset()
            );
            let dateString = ResolutionDate.getShortFormat(new Date(eventTime));
            if ($clientSettings.display.showRelativeToday) {
                dateString = getHumanFriendlyDateDifferenceForToday(eventTime);
            }
            let timeString = ResolutionTime.getDateStringFromMinutes(minutesUnix);
            return dateString + ', ' + timeString;
        } catch (e) {
            console.error(e);
            return eventTime;
        }
    }

    onMount(() => {
        if (extended) {
            expandedAll = true;
        }
        reloadData();
    });

    $: {
        jobHistoryListVisible = jobHistoryApp.getJobHistoryListVisible(
            extended ? null : 5,
            !$debug
        );
    }

    $: {
        console.log('jobHistoryList', job);
        if (job !== undefined) {
            reloadData();
        }
    }
</script>

<div>
    {#if extended}
        <div style="display: flex; justify-content: space-between">
            <div class="job-history-title">{$_('Job history')} #{jobId}</div>
            {#if $debug}
                <Switch bind:checked={showDetails} label="Show undefined details (debug)" />
            {/if}
        </div>
    {/if}
    {#if loading && jobHistoryApp.getJobHistoryList().length === 0}
        <Spinner />
    {:else}
        <div class="job-history-header job-history-element">
            <div>{$_('Date')}</div>
            <div>{$_('Status')}</div>
            <div>{$_('Info')}</div>
        </div>
        <div class="job-history-list">
            {#if error}
                <div style="display: flex; justify-content: center">
                    <ErrorInline text={error} />
                </div>
            {:else}
                {#each jobHistoryListVisible as h}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                    <div class="job-history-item job-history-element" class:odd={!h.isVisible()}>
                        <div
                            class="job-history-item-date dont-break"
                            on:click={() => {
                                console.debug(
                                    h.getData().event_id +
                                        ' RAW: ' +
                                        JSON.stringify(h.getData(), null, 1)
                                );
                                console.debug(
                                    h.getData().event_id +
                                        ' this.diffs: ' +
                                        JSON.stringify(h.diffs, null, 1)
                                );
                            }}
                        >
                            {getFormattedEventTime(h.getData().event_time)}
                        </div>
                        <div class="job-history-item-description" style="display: flex;">
                            <JobHistoryDescription
                                jobHistoryItem={h}
                                {showDetails}
                                {jobHistoryApp}
                            />
                        </div>
                        <div class="job-history-item-source">
                            <JobHistoryEventSource
                                jobHistoryItem={h}
                                {showDetails}
                                {jobHistoryApp}
                            />
                        </div>
                    </div>
                {:else}
                    <MessageNotFound text={$_('No job history')} />
                {/each}
            {/if}
        </div>
        <div style="display:flex; justify-content: center; margin-top:5px;">
            {#if !extended && (jobHistoryApp.getJobHistoryListVisible().length > showLimit || true)}
                <ButtonSmallBlueRounded
                    on:click={() => {
                        openModal(ModalCustom, {
                            component: JobHistoryWrapper,
                            componentProps: { jobId, extended: true },
                        });
                    }}
                    text={$_('Show all') + ` (${jobHistoryApp.getJobHistoryListVisible().length})`}
                />
            {/if}
        </div>
    {/if}
</div>

<style>
    .job-history-title {
        font-size: 1.2em;
        font-weight: bold;
        margin-bottom: 10px;
    }

    .job-history-list {
        border-bottom: var(--list-element-data-border-bottom);
        font-size: var(--font-size-tactical-plan);
        overflow-x: auto;
        max-height: 429px;
        padding-right: 10px;
        overflow-y: auto;
        flex: 1;
    }

    .job-history-element {
        display: grid;
        grid-template-columns: 0.25fr 0.6fr 0.4fr;
        grid-template-rows: min-content;
        column-gap: 5px;
        padding: 5px 0;
    }

    .job-history-header {
        position: relative;
        align-items: flex-end;
        padding-bottom: 5px;
        padding-right: 10px;
        border-bottom: var(--list-element-header-border-bottom);
        color: var(--list-element-header-color-inactive);
        font-size: var(--list-element-header-font-size);
    }

    .job-history-item {
        cursor: pointer;
        align-items: center;
        column-gap: 5px;
        border-bottom: var(--list-element-data-border-bottom);
        font-size: var(--font-size-tactical-plan);
    }

    /*.job-history-item-description {*/
    /*    color: #1c5dc7;*/
    /*}*/
    /*.job-history-item-date {*/
    /*    color: #1c5dc7;*/
    /*}*/
    .odd {
        background-color: #dadada;
    }
</style>
